/**
 * Clickable marketplace card for a Bounty or SubmissionDisplay: square cover
 * image (first s3ImageUrl, falling back to a dark panel showing the
 * description), a reward badge when the item has a nonzero reward, and the
 * category/title below. Purely presentational; navigation happens in the
 * onClick prop. Rendered by ItemCollection for browse/listings/pursuits/
 * submissions grids and rows.
 */
import React from "react";
import { Bounty } from "@/types/bounty";
import { SubmissionDisplay } from "@/types/submission";

type CardItem = Bounty | SubmissionDisplay;

interface ItemCardProps {
    item: CardItem;
    onClick: () => void;
    className?: string;
}

const ItemCard: React.FC<ItemCardProps> = ({ item, onClick, className }) => {
    const { title, s3ImageUrls } = item;

    const description = "description" in item ? item.description : "";
    const category = "category" in item ? item.category : null;

    return (
        <div
            className={`group relative bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100 
                hover:shadow-xl hover:border-slate-200 hover:-translate-y-1
                transition-all duration-300 cursor-pointer isolate will-change-transform ${className}`}
            onClick={onClick}
        >
            <div className="aspect-square overflow-hidden bg-slate-50 relative rounded-t-2xl">
                {"reward" in item && item.reward != null && item.reward !== 0 && (
                    <div className="absolute top-3 right-3 z-10">
                        <div className="font-porsui text-sm font-medium text-slate-900 bg-white/90 backdrop-blur-sm shadow-lg px-3 py-1.5 rounded-full">
                            ${item.reward}
                        </div>
                    </div>
                )}
                {s3ImageUrls && s3ImageUrls.length > 0 ? (
                    <img
                        src={s3ImageUrls[0]}
                        alt={title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                    />
                ) : (
                    <div className="w-full h-full bg-slate-900 flex items-center justify-center p-6">
                        <span className="font-porsui text-sm text-white/80 text-center line-clamp-4 leading-relaxed">
                            {description}
                        </span>
                    </div>
                )}
            </div>
            <div className="p-4">
                {category && (
                    <p className="font-porsui text-[10px] text-slate-400 uppercase tracking-wider mb-1">
                        {category}
                    </p>
                )}
                <h3 className="font-porsui text-sm text-slate-800 line-clamp-2 leading-snug">
                    {title}
                </h3>
            </div>
        </div>
    );
};

export default ItemCard;
